import React from 'react'
import withSSR from 'js/components/withSSR/withSSR'
import Spinner from 'js/components/Spinner/Spinner'
import ErrorPage from 'js/pages/ErrorPage'
import { runQuery, getDatoLocale, datoLocalesToLanguages } from 'js/dato'
import {
  categoryDataQuery,
  allStoriesQuery,
  recipesDataQuery,
} from 'js/pages/NutritionExplained/queries/categoryQuery'
import Footer from 'js/components/Footer/Footer'
import { Navigation } from 'js/pages/Home/Navigation'
import CategoryListing from './CategoryListing'
import FeaturedStories from './FeaturedStories'
import EmptyState from './EmptyState'
import { Tabs, Tab } from './Tabs'
import DatoMeta from '../../../components/DatoMeta'
import { CategoryData_category } from '../queries/types/CategoryData'
import { AllStoriesCategory } from '../queries/types/AllStoriesCategory'
import { FeatureImage, FeatureImageType } from 'js/components/FeatureImage'

type Props = {
  data:
    | ({
        category: {
          type: 'recipes'
          recipeCount: number
          image: {
            responsiveImage: FeatureImageType
          } | null
        } & CategoryData_category
        allRecipes: {}
      } & Pick<AllStoriesCategory, 'allRecipes'>)
    | ({
        category: {
          type: 'category'
          storyCount: number
          recipeCount: number
          image: {
            responsiveImage: FeatureImageType
          } | null
        } & CategoryData_category
      } & Pick<
        AllStoriesCategory,
        'featuredStories' | 'allRecipes' | 'allStories'
      >)
  isLoading: boolean
  error: any
}

function NutritionCategory({ data, isLoading, error }: Props) {
  if (isLoading)
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <Spinner width="50" color="green" />
      </div>
    )

  if (error) {
    return <ErrorPage error={error} />
  }

  const { category } = data
  const availableLanguages = datoLocalesToLanguages(category._locales ?? ['en'])

  const hasBackgroundImage =
    (category.type === 'category' || category.type === 'recipes') &&
    category.image

  return (
    <>
      <DatoMeta
        meta={category.meta}
        fallbackDescription={category.description as string}
        canonicalUrl={`https://lifesum.com/nutrition-explained/category/${category.slug}`}
      />
      <div className="bg-LSGreenDarkest py-20">
        <Navigation
          theme="dark"
          minimal={false}
          availableLanguages={availableLanguages}
        />
      </div>
      <div className="relative bg-BgMain">
        <div className="h-[200px] md:h-[400px]">
          <div
            className={`absolute inset-0 flex h-[200px] bg-LSGreenDarkest md:h-[400px]`}
          >
            <h1
              className={
                'type-huge-fat sm:trans quincy align-end o-width-limiter absolute bottom-0 py-24 text-white sm:px-16 lg:my-auto lg:py-40'
              }
            >
              {category.name}
            </h1>
            {hasBackgroundImage && category.image && (
              <FeatureImage
                image={category.image.responsiveImage as FeatureImageType}
                className="h-full  w-full translate-x-2/4 object-contain"
              />
            )}
          </div>
        </div>
        <div className="mb-20 mt-40 pb-40 lg:mt-80 lg:pb-64">
          {category.description && (
            <div className="o-width-limiter">
              <p className="type-med-minus mx-auto max-w-768 text-center">
                {category.description}
              </p>
            </div>
          )}
        </div>
        <FeaturedStories stories={data['featuredStories'] || []} />
        <Tabs>
          <Tab type="stories" count={category['storyCount']}>
            <CategoryListing
              key="stories"
              type="stories"
              initialEntries={data['allStories'] || []}
              category={category}
              showHeading={!data.allRecipes || !data.allRecipes?.length}
            />
          </Tab>
          <Tab type="recipes" count={category.recipeCount}>
            <CategoryListing
              key="recipes"
              type="recipes"
              initialEntries={data.allRecipes || []}
              category={category}
              showHeading={
                category.type === 'recipes'
                  ? false
                  : !data['allStories'] || !data['allStories'].length
              }
            />
          </Tab>
        </Tabs>
        {(!data.allRecipes || data.allRecipes.length === 0) &&
          (!data['allStories'] || data['allStories'].length === 0) && (
            <EmptyState />
          )}
        {/*<div className="mt-56 md:mt-72 lg:mt-128">
          <AppPromo />
          </div>*/}
        <Footer marginless beta />
      </div>
    </>
  )
}

NutritionCategory.getInitialProps = ({ params, language }) => {
  const locale = getDatoLocale(language || 'en')

  if (params.category)
    return runQuery(categoryDataQuery, { slug: params.category, locale }).then(
      ({ category }) => {
        if (!category) {
          throw new Error(`category not found, category: ${params.category}`)
        }

        return runQuery(allStoriesQuery, { id: category.id }).then(
          ({ meta, recipesMeta, allStories, allRecipes, featuredStories }) => {
            return {
              category: {
                type: 'category',
                storyCount: meta.count,
                recipeCount: recipesMeta.count,
                ...category,
              },
              featuredStories: featuredStories,
              allStories: allStories,
              allRecipes: allRecipes,
            }
          },
        )
      },
    )

  return Promise.all([
    runQuery(categoryDataQuery, { slug: 'recipes', locale }),
    runQuery(recipesDataQuery),
  ]).then(([{ category }, { meta, allRecipes }]) => {
    return {
      category: {
        type: 'recipes',
        recipeCount: meta.count,
        ...category,
      },
      allRecipes: allRecipes,
    }
  })
}

export default withSSR(NutritionCategory)
